import { useState } from 'react';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

import Alert from '@/components/Alert';
import Button from '@/components/Button';
import Card from '@/components/Card';
import FormElement from '@/components/FormElement';
import Modal from '@/components/Modal';
import Stack from '@/components/Stack';
import TextArea from '@/components/TextArea';
import { Body } from '@/components/Typography';

type Props = {
  workerName: string;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void>;
};

const LiftSuspensionModal = ({ workerName, onClose, onSubmit }: Props) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!reason.trim()) return;

    setLoading(true);
    setError(null);

    try {
      await onSubmit(reason.trim());
      onClose();
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to lift suspension';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      disableClickout
      size="sm"
      title="Lift Worker Suspension"
      onRequestClose={onClose}
    >
      <Card.Section>
        <Body>
          You are about to lift the suspension for <strong>{workerName}</strong>
          . Please add a reason to proceed.
        </Body>
      </Card.Section>

      <Card.Section>
        <Stack vertical gap={12}>
          {error && (
            <Alert
              description={error}
              icon={faExclamationTriangle}
              status="danger"
              title="Error"
            />
          )}

          <FormElement label="Reason (required)" required showRequiredIndicator>
            <TextArea
              placeholder="Add reason for lifting this suspension"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
            />
          </FormElement>
        </Stack>
      </Card.Section>

      <Card.Section>
        <Stack justify="end">
          <Button
            a11yLabel="Cancel lift suspension"
            appearance="outline"
            label="Cancel"
            onClick={onClose}
          />
          <Button
            a11yLabel="Confirm lift worker suspension"
            isLoading={loading}
            label="Confirm Lift Suspension"
            status="success"
            onClick={handleSubmit}
            disabled={!reason.trim()}
          />
        </Stack>
      </Card.Section>
    </Modal>
  );
};

export default LiftSuspensionModal;
